// Navbar2.js - Contains functions for creating a modern Navbar section with dropdown menus
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete modern Navbar section with nested elements including dropdown functionality
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the navbar is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The navbar elements
 */
export const createNavbarElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const navbarSectionClass = 'navbar2-section';
  const navbarContainerClass = 'navbar2-container';
  const navbarLogoClass = 'navbar2-logo';
  const navbarMenuClass = 'navbar2-menu';
  const navbarItemClass = 'navbar2-item';
  const navbarLinkClass = 'navbar2-link';
  const navbarSubmenuClass = 'navbar2-submenu';
  const navbarSubmenuItemClass = 'navbar2-submenu-item';
  const navbarSubmenuLinkClass = 'navbar2-submenu-link';
  const navbarCartClass = 'navbar2-cart';
  const navbarContactBtnClass = 'navbar2-contact-btn';
  const navbarMobileToggleClass = 'navbar2-mobile-toggle';
  const navbarMobileToggleIconClass = 'navbar-mobile-toggle-icon';
  const navbarActiveSubmenuClass = 'active-submenu';
  const navbarMobileMenuShowClass = 'navbar-mobilemenu-show';
  const navbarLinkArrowClass = 'navbar-link-arrow';
  const navbarLinkArrowUpClass = 'navbar2-link-arrow-up';
  const navbarCartIconClass = 'navbar2-cart-icon';
  const navbarCartCountClass = 'navbar2-cart-count';

  // Desktop Styles (default)
  const navbarSectionStyles = {
    position: 'relative',
    width: '100%',
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    zIndex: '100',
    padding: '10px 0'
  };

  const navbarContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  };

  const navbarLogoStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#333333',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center'
  };

  const navbarMenuStyles = {
    display: 'flex',
    listStyle: 'none',
    margin: '0',
    padding: '0',
    gap: '30px'
  };

  const navbarItemStyles = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };

  const navbarLinkStyles = {
    color: '#333333',
    textDecoration: 'none',
    fontSize: '16px',
    fontWeight: '500',
    padding: '8px 0',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  };

  const navbarLinkArrowStyles = {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    marginLeft: '4px',
    backgroundImage: 'url("https://cdn-icons-png.flaticon.com/512/60/60995.png")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    transition: 'transform 0.3s ease',
    opacity: '0.7'
  };

  const navbarLinkArrowUpStyles = {
    transform: 'rotate(180deg)'
  };

  const navbarSubmenuStyles = {
    position: 'absolute',
    top: '100%',
    left: '0',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    borderRadius: '4px',
    minWidth: '200px',
    padding: '8px 0',
    opacity: '0',
    visibility: 'hidden',
    transform: 'translateY(10px)',
    transition: 'all 0.3s ease',
    zIndex: '200'
  };

  const navbarActiveSubmenuStyles = {
    opacity: '1',
    visibility: 'visible',
    transform: 'translateY(0)'
  };

  const navbarSubmenuItemStyles = {
    display: 'block',
    margin: '0'
  };

  const navbarSubmenuLinkStyles = {
    display: 'block',
    color: '#333333',
    textDecoration: 'none',
    fontSize: '14px',
    padding: '8px 16px',
    transition: 'all 0.2s ease'
  };

  const navbarCartStyles = {
    position: 'relative',
    marginRight: '15px'
  };

  const navbarCartIconStyles = {
    width: '24px',
    height: '24px',
    display: 'inline-block',
    backgroundImage: 'url("https://cdn-icons-png.flaticon.com/512/2838/2838895.png")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  const navbarCartCountStyles = {
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    backgroundColor: '#FFC107',
    color: '#333',
    fontSize: '12px',
    fontWeight: 'bold',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const navbarContactBtnStyles = {
    backgroundColor: '#FFC107',
    color: '#333',
    padding: '10px 20px',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: '14px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    display: 'inline-block'
  };

  const navbarMobileToggleStyles = {
    display: 'none',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const navbarMobileToggleIconStyles = {
    width: '24px',
    height: '24px',
    display: 'inline-block',
    backgroundImage: 'url("https://cdn-icons-png.flaticon.com/512/5358/5358649.png")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    transition: 'transform 0.2s ease'
  };

  // Define the mobile menu show class styles
  const navbarMobileMenuShowStyles = {
    display: 'flex'
  };

  // Tablet & Mobile Styles
  const tabletNavbarMenuStyles = {
    ...navbarMenuStyles,
    gap: '15px'
  };

  const mobileNavbarContainerStyles = {
    ...navbarContainerStyles,
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  };

  const mobileNavbarMenuStyles = {
    display: 'none',
    flexDirection: 'column',
    width: '100%',
    gap: '5px',
    marginTop: '16px'
  };

  const mobileNavbarMobileToggleStyles = {
    ...navbarMobileToggleStyles,
    display: 'block'
  };

  const mobileNavbarItemStyles = {
    ...navbarItemStyles,
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    borderBottom: '1px solid #eee',
    padding: '8px 0'
  };

  const mobileNavbarSubmenuStyles = {
    ...navbarSubmenuStyles,
    position: 'relative',
    top: '0',
    boxShadow: 'none',
    width: '100%',
    maxHeight: '0',
    overflow: 'hidden',
    padding: '0',
    transition: 'max-height 0.3s ease',
    opacity: '1',
    visibility: 'visible',
    transform: 'none'
  };

  const mobileNavbarActiveSubmenuStyles = {
    maxHeight: '500px',
    padding: '8px 0 8px 15px'
  };

  const mobileNavbarContactBtnStyles = {
    ...navbarContactBtnStyles,
    width: '100%',
    textAlign: 'center',
    marginTop: '10px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(navbarSectionClass, navbarSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContainerClass, navbarContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarLogoClass, navbarLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMenuClass, navbarMenuStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarItemClass, navbarItemStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarLinkClass, navbarLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarLinkArrowClass, navbarLinkArrowStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarLinkArrowUpClass, navbarLinkArrowUpStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarSubmenuClass, navbarSubmenuStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarSubmenuItemClass, navbarSubmenuItemStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarSubmenuLinkClass, navbarSubmenuLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarCartClass, navbarCartStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarCartIconClass, navbarCartIconStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarCartCountClass, navbarCartCountStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContactBtnClass, navbarContactBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMobileToggleClass, navbarMobileToggleStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMobileToggleIconClass, navbarMobileToggleIconStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarActiveSubmenuClass, navbarActiveSubmenuStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMobileMenuShowClass, navbarMobileMenuShowStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(navbarMenuClass, tabletNavbarMenuStyles);
    newRegistry[DEVICES.TABLET].set(navbarMobileMenuShowClass, navbarMobileMenuShowStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(navbarContainerClass, mobileNavbarContainerStyles);
    newRegistry[DEVICES.MOBILE].set(navbarMenuClass, mobileNavbarMenuStyles);
    newRegistry[DEVICES.MOBILE].set(navbarMobileToggleClass, mobileNavbarMobileToggleStyles);
    newRegistry[DEVICES.MOBILE].set(navbarMobileToggleIconClass, navbarMobileToggleIconStyles);
    newRegistry[DEVICES.MOBILE].set(navbarItemClass, mobileNavbarItemStyles);
    newRegistry[DEVICES.MOBILE].set(navbarSubmenuClass, mobileNavbarSubmenuStyles);
    newRegistry[DEVICES.MOBILE].set(navbarActiveSubmenuClass, mobileNavbarActiveSubmenuStyles);
    newRegistry[DEVICES.MOBILE].set(navbarMobileMenuShowClass, navbarMobileMenuShowStyles);
    newRegistry[DEVICES.MOBILE].set(navbarContactBtnClass, mobileNavbarContactBtnStyles);
    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    navbarSectionClass,
    navbarContainerClass,
    navbarLogoClass,
    navbarMenuClass,
    navbarItemClass,
    navbarLinkClass,
    navbarLinkArrowClass,
    navbarLinkArrowUpClass,
    navbarSubmenuClass,
    navbarSubmenuItemClass,
    navbarSubmenuLinkClass,
    navbarCartClass,
    navbarCartIconClass,
    navbarCartCountClass,
    navbarContactBtnClass,
    navbarMobileToggleClass,
    navbarMobileToggleIconClass,
    navbarActiveSubmenuClass,
    navbarMobileMenuShowClass,
  ]));

  // Generate IDs for all elements
  const navbarSectionId = Date.now();
  const navbarContainerId = navbarSectionId + 1;
  const navbarLogoId = navbarSectionId + 2;
  const navbarMenuId = navbarSectionId + 3;
  const navbarCartId = navbarSectionId + 4;
  const navbarContactBtnId = navbarSectionId + 5;
  const navbarMobileToggleId = navbarSectionId + 6;

  // Menu items (navigation links)
  const menuItems = [
    { name: 'Demo', link: '#', hasSubmenu: true, submenuItems: [
      { name: 'Demo Option 1', link: '#' },
      { name: 'Demo Option 2', link: '#' },
      { name: 'Demo Option 3', link: '#' }
    ]},
    { name: 'About Us', link: '#', hasSubmenu: true, submenuItems: [
      { name: 'About Us', link: '#' },
      { name: 'Our Team', link: '#' },
      { name: 'Pricing', link: '#' },
      { name: 'Career', link: '#' }
    ]},
    { name: 'Services', link: '#', hasSubmenu: false },
    { name: 'Shop', link: '#', hasSubmenu: false },
    { name: 'All Pages', link: '#', hasSubmenu: true, submenuItems: [
      { name: 'Page 1', link: '#' },
      { name: 'Page 2', link: '#' },
      { name: 'Page 3', link: '#' }
    ]}
  ];

  // Create element IDs for menu items and submenus
  let currentId = navbarMobileToggleId + 1;
  const menuItemElements = [];

  menuItems.forEach((item, index) => {
    const menuItemId = currentId++;
    const linkId = currentId++;
    let submenuId = null;
    let arrowIconId = null;
    const submenuItems = [];

    if (item.hasSubmenu) {
      submenuId = currentId++;
      arrowIconId = currentId++;
      
      // Create submenu items
      item.submenuItems.forEach(submenuItem => {
        const submenuItemId = currentId++;
        const submenuLinkId = currentId++;
        
        submenuItems.push({
          id: submenuItemId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'li',
          name: `${item.name} Submenu Item - ${submenuItem.name}`,
          hierarchyName: `${item.name} Submenu Item - ${submenuItem.name}`,
          parentId: submenuId,
          className: navbarSubmenuItemClass,
          styles: {},
          children: [submenuLinkId],
          text: '',
          attributes: {
            'data-element': 'navbar-submenu-item'
          }
        });

        submenuItems.push({
          id: submenuLinkId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'link',
          name: `${submenuItem.name} Link`,
          hierarchyName: `${submenuItem.name} Link`,
          parentId: submenuItemId,
          className: navbarSubmenuLinkClass,
          styles: {},
          children: [],
          text: submenuItem.name,
          attributes: {
            'data-element': 'navbar-submenu-link',
            'href': submenuItem.link
          }
        });
      });
    }

    // Create menu item element
    menuItemElements.push({
      id: menuItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: `${item.name} Menu Item`,
      hierarchyName: `${item.name} Menu Item`,
      parentId: navbarMenuId,
      className: navbarItemClass,
      styles: {},
      children: item.hasSubmenu ? [linkId, submenuId] : [linkId],
      text: '',
      attributes: {
        'data-element': 'navbar-item',
        'data-has-submenu': item.hasSubmenu.toString()
      },
      // Add hover interaction to add class to submenu directly
      interactions: item.hasSubmenu ? {
        events: [
          {
            id: Date.now() + index,
            type: 'hover',
            action: 'toggleClass',
            direction: 'in',
            label: `Hover In: Add active class to submenu`,
            value: {
              targetId: submenuId, // Target the submenu directly
              transitionDuration: '0.3s',
              className: navbarActiveSubmenuClass
            }
          },
          {
            id: Date.now() + index + 1000,
            type: 'hover',
            action: 'toggleClass',
            direction: 'out',
            label: `Hover Out: Remove active class from submenu`,
            value: {
              targetId: submenuId, // Target the submenu directly
              className: navbarActiveSubmenuClass,
              remove: true
            }
          },
          {
            id: Date.now() + index + 2000,
            type: 'hover',
            action: 'toggleClass',
            direction: 'in',
            label: `Hover In: Rotate arrow icon`,
            value: {
              targetId: arrowIconId,
              className: navbarLinkArrowUpClass
            }
          },
          {
            id: Date.now() + index + 3000,
            type: 'hover',
            action: 'toggleClass',
            direction: 'out',
            label: `Hover Out: Restore arrow icon`,
            value: {
              targetId: arrowIconId,
              className: navbarLinkArrowUpClass,
              remove: true
            }
          }
        ]
      } : {}
    });

    // Create menu item link and arrow icon if needed
    if (item.hasSubmenu) {
      menuItemElements.push({
        id: linkId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'link',
        name: `${item.name} Link`,
        hierarchyName: `${item.name} Link`,
        parentId: menuItemId,
        className: navbarLinkClass,
        styles: {},
        children: [arrowIconId],
        text: item.name,
        attributes: {
          'data-element': 'navbar-link',
          'href': item.link
        }
      });

      // Create arrow icon element
      menuItemElements.push({
        id: arrowIconId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `${item.name} Arrow Icon`,
        hierarchyName: `${item.name} Arrow Icon`,
        parentId: linkId,
        className: navbarLinkArrowClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'navbar-link-arrow',
          'aria-hidden': 'true'
        }
      });
    } else {
      menuItemElements.push({
        id: linkId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'link',
        name: `${item.name} Link`,
        hierarchyName: `${item.name} Link`,
        parentId: menuItemId,
        className: navbarLinkClass,
        styles: {},
        children: [],
        text: item.name,
        attributes: {
          'data-element': 'navbar-link',
          'href': item.link
        }
      });
    }

    // Create submenu if needed
    if (item.hasSubmenu) {
      // Create submenu container
      menuItemElements.push({
        id: submenuId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'ul',
        name: `${item.name} Submenu`,
        hierarchyName: `${item.name} Submenu`,
        parentId: menuItemId,
        className: navbarSubmenuClass,
        styles: {},
        children: submenuItems.filter(item => item.type === 'li').map(item => item.id),
        text: '',
        attributes: {
          'data-element': 'navbar-submenu'
        }
      });

      // Add submenu items to the elements array
      menuItemElements.push(...submenuItems);
    }
  });

  const elementIdsForMenu = menuItemElements
    .filter(element => element.parentId === navbarMenuId)
    .map(element => element.id);

  // Create base navbar elements
  const baseElements = [
    // Main Navbar Section
    {
      id: navbarSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'nav',
      name: 'Navbar Section',
      hierarchyName: 'Navbar Section',
      parentId: null,
      className: navbarSectionClass,
      styles: {},
      children: [navbarContainerId],
      text: '',
      attributes: {
        'data-element': 'navbar-section',
        'data-component': 'nav',
        'data-section-type': 'navbar',
        'data-page-id': currentPageId,
        'role': 'navigation',
        'aria-label': 'Main navigation'
      }
    },
    // Container
    {
      id: navbarContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Navbar Container',
      hierarchyName: 'Navbar Container',
      parentId: navbarSectionId,
      className: navbarContainerClass,
      styles: {},
      children: [navbarLogoId, navbarMobileToggleId, navbarMenuId, navbarCartId, navbarContactBtnId],
      text: '',
      attributes: {
        'data-element': 'navbar-container'
      }
    },
    // Logo
    {
      id: navbarLogoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Navbar Logo',
      hierarchyName: 'Navbar Logo',
      parentId: navbarContainerId,
      className: navbarLogoClass,
      styles: {},
      children: [],
      text: 'RELOCATION',
      attributes: {
        'data-element': 'navbar-logo',
        'href': '#'
      }
    },
    // Cart
    {
      id: navbarCartId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Cart',
      hierarchyName: 'Cart',
      parentId: navbarContainerId,
      className: navbarCartClass,
      styles: {},
      children: [navbarCartId + 100, navbarCartId + 101],
      text: '',
      attributes: {
        'data-element': 'navbar-cart',
        'href': '#',
        'aria-label': 'Shopping cart'
      }
    },
    // Cart Icon
    {
      id: navbarCartId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Cart Icon',
      hierarchyName: 'Cart Icon',
      parentId: navbarCartId,
      className: navbarCartIconClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'navbar-cart-icon',
        'aria-hidden': 'true'
      }
    },
    // Cart Count
    {
      id: navbarCartId + 101,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Cart Count',
      hierarchyName: 'Cart Count',
      parentId: navbarCartId,
      className: navbarCartCountClass,
      styles: {},
      children: [],
      text: '0',
      attributes: {
        'data-element': 'navbar-cart-count',
        'aria-label': 'Item count'
      }
    },
    // Contact Button
    {
      id: navbarContactBtnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Contact Button',
      hierarchyName: 'Contact Button',
      parentId: navbarContainerId,
      className: navbarContactBtnClass,
      styles: {},
      children: [],
      text: 'CONTACT US',
      attributes: {
        'data-element': 'navbar-contact-btn',
        'href': '#',
        'role': 'button'
      }
    },
    // Mobile Toggle Button
    {
      id: navbarMobileToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Mobile Toggle',
      hierarchyName: 'Mobile Toggle',
      parentId: navbarContainerId,
      className: navbarMobileToggleClass,
      styles: {},
      children: [navbarMobileToggleId + 100], // Add span child
      text: '',
      attributes: {
        'data-element': 'navbar-mobile-toggle',
        'aria-label': 'Toggle navigation menu',
      },
      interactions: {
        events: [
          {
            id: Date.now() + 100,
            type: 'click',
            action: 'toggleClass',
            sequence: 'first',
            label: '1st Click: Toggle Class: navbar-mobilemenu-show on navbar-menu',
            value: {
              targetId: navbarMenuId,
              className: navbarMobileMenuShowClass
            }
          },
          {
            id: Date.now() + 101,
            type: 'click',
            action: 'toggleClass',
            sequence: 'second',
            label: '2nd Click: Toggle Class: navbar-mobilemenu-hide on navbar-menu',
            value: {
              targetId: navbarMenuId,
              className: navbarMobileMenuShowClass
            }
          }
        ]
      }
    },
    // Mobile Toggle Icon
    {
      id: navbarMobileToggleId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Mobile Toggle Icon',
      hierarchyName: 'Mobile Toggle Icon',
      parentId: navbarMobileToggleId,
      className: navbarMobileToggleIconClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'navbar-mobile-toggle-icon',
        'aria-hidden': 'true'
      }
    },
    // Menu Container
    {
      id: navbarMenuId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'ul',
      name: 'Navbar Menu',
      hierarchyName: 'Navbar Menu',
      parentId: navbarContainerId,
      className: navbarMenuClass,
      styles: {},
      children: elementIdsForMenu,
      text: '',
      attributes: {
        'data-element': 'navbar-menu'
      }
    }
  ];

  // Combine all elements
  const allElements = [...baseElements, ...menuItemElements];
  
  // Return the created elements
  return {
    navbarSection: allElements[0],
    elements: allElements
  };
};

export default createNavbarElements; 